import type { CSSProperties, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { routes } from '@/lib/routes';
import logo from '@/assets/logo.svg';

export type Owner10HubTab = {
  id: string;
  label: string;
};

export type Owner10SubCard = {
  id: string;
  code: string;
  title: string;
  text: string;
  accent: string;
  cta: string;
  meta?: string[];
  onOpen: () => void;
};

type Owner10HubShellProps = {
  kicker?: string;
  title: string;
  subtitle: string;
  onClose?: () => void;
  onBack?: () => void;
  tabs?: Owner10HubTab[];
  activeTab?: string;
  onTabChange?: (id: string) => void;
  cards?: Owner10SubCard[];
  note?: string;
  children?: ReactNode;
  /** Kept for callers; section destinations always open as full-screen cabinet overlay. */
  mode?: 'page' | 'overlay';
  open?: boolean;
};

export const Owner10HubShell = ({
  kicker = 'Владелец системы · Zertteu Academy',
  title,
  subtitle,
  onClose,
  onBack,
  tabs,
  activeTab,
  onTabChange,
  cards,
  note,
  children,
  open = true,
}: Owner10HubShellProps) => {
  const navigate = useNavigate();
  if (!open) return null;

  const handleClose = () => {
    if (onClose) onClose();
    else navigate(routes.owner);
  };
  const handleBack = () => {
    if (onBack) onBack();
    else handleClose();
  };

  return (
    <div className="cabinet-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="cabinet-overlay-shell">
        <header className="cabinet-overlay-top">
          <div className="cabinet-brandmark" aria-hidden="true">
            <img src={logo} alt="" />
          </div>
          <div className="cabinet-title">
            <small>{kicker}</small>
            <h1>{title}</h1>
            <p>{subtitle}</p>
            {tabs && tabs.length > 0 ? (
              <nav className="cabinet-overlay-nav" aria-label="Разделы модуля">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={activeTab === tab.id ? 'is-active' : ''}
                    onClick={() => onTabChange?.(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            ) : null}
          </div>
          <div className="cabinet-overlay-actions">
            <button type="button" className="cabinet-btn" onClick={handleBack}>
              ← Назад
            </button>
            <button type="button" className="cabinet-btn" onClick={handleClose}>
              Закрыть ×
            </button>
          </div>
        </header>

        <div className="cabinet-overlay-body">
          <div className="cabinet-page">
            {cards && cards.length > 0 ? (
              <div className="cabinet-grid3">
                {cards.map((card) => (
                  <article
                    key={card.id}
                    className="cabinet-card"
                    style={{ '--accent': card.accent } as CSSProperties}
                  >
                    <small className="cabinet-eyebrow">{card.code}</small>
                    <h3>{card.title}</h3>
                    <p>{card.text}</p>
                    {card.meta && card.meta.length > 0 ? (
                      <div className="cabinet-actions" style={{ marginTop: 8 }}>
                        {card.meta.map((item) => (
                          <span key={item} className="cabinet-badge" style={{ '--badge': card.accent } as CSSProperties}>
                            {item}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    <div className="cabinet-actions">
                      <button type="button" className="cabinet-btn primary" onClick={card.onOpen}>
                        {card.cta} →
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : null}

            {children}

            {note ? <div className="cabinet-note">{note}</div> : null}

            {!children && !cards?.length && !note ? (
              <div className="cabinet-note">
                Раздел открыт. Вернитесь в{' '}
                <Link to={routes.owner} style={{ color: 'var(--cabinet-gold)' }}>
                  кабинет владельца
                </Link>
                .
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
